import { ReactNode } from 'react';

import classNames from 'classnames';

import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';

import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

import styles from './settings-edit-panel.module.css';

interface ISettingsEditPanelProps {
  children: ReactNode;
  expandContent?: ReactNode;
  isEditing: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
  onSave?: () => void;
  footerLeftContent?: ReactNode;
  warningMessage?: string;
  saveDisabled?: boolean;
  disabled?: boolean;
}

const SettingsEditPanel = ({
  children,
  expandContent,
  isEditing,
  onCancel,
  cancelLabel = 'Cancel',
  onSave,
  footerLeftContent,
  warningMessage,
  saveDisabled,
  disabled,
}: ISettingsEditPanelProps) => {
  const isSaving = useAppSelector(selectIsSavingWorkingProject);
  const hasFooter = !!(onSave || onCancel || footerLeftContent || warningMessage);

  return (
    <Container
      className={classNames('flex-column', { [styles.isDisabled]: disabled })}
      design="GlobalCont1"
      borderRadius="12px"
    >
      {children}
      {expandContent && (
        <Container className={classNames(styles.expandWrap, { [styles.open]: isEditing })} inert={!isEditing}>
          <Container className="overflow-hidden" minHeight="0px">
            {expandContent}
          </Container>
        </Container>
      )}
      {hasFooter && (
        <Container className={classNames(styles.footer, { [styles.footerOpen]: isEditing })} inert={!isEditing}>
          <Container className="overflow-hidden" minHeight="0px">
            <Container className="flex ai-center jc-space-between" padding="16px">
              {footerLeftContent ?? (
                <Text className="P2" design="GlobalError400">
                  {warningMessage}
                </Text>
              )}
              <Container className="flex ai-center gap-8">
                <OptionalComponent display={!!onCancel}>
                  <Button
                    design="LocalBttnSec"
                    height="32px"
                    className="L2 padding-v-7 padding-h-24"
                    onClick={onCancel}
                    disabled={isSaving || saveDisabled}
                  >
                    <Text className="L2">{cancelLabel}</Text>
                  </Button>
                </OptionalComponent>
                <OptionalComponent display={!!onSave}>
                  <Button
                    backgroundColor={getThemeColorCSSVariable()}
                    padding="8px 24px"
                    width="auto"
                    onClick={onSave}
                    disabled={isSaving || saveDisabled}
                    disabledOpacity={0.6}
                    hoverOpacity={0.8}
                  >
                    <Text color={getThemeColorContrastCSSVariable()} className="L2">
                      Save
                    </Text>
                  </Button>
                </OptionalComponent>
              </Container>
            </Container>
          </Container>
        </Container>
      )}
    </Container>
  );
};

export default SettingsEditPanel;
